import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ExternalLink, Sparkles, BookOpen, Users, GraduationCap, Shield, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default apps (fallback if database is empty)
const defaultApps = {
  kids: [
    {
      id: '1',
      name: "Barrett's Joyful Christmas Kids Tale",
      description: "An interactive AI-powered Christmas story adventure designed to spark joy and creativity in children.",
      age_range: "Ages 4-9",
      duration: "5-10 minutes",
      url: "https://barretts-joyful-christmas-kids-tale.vercel.app/",
      icon: "🎄",
      bg_color: "from-red-100 to-green-100",
      border_color: "border-red-400",
      is_coming_soon: false
    }
  ],
  parents: [
    {
      id: '2',
      name: "Coming Soon",
      description: "Educational tools and resources for parents and clients to enhance learning at home.",
      icon: "📚",
      bg_color: "from-blue-50 to-indigo-50",
      border_color: "border-blue-300",
      is_coming_soon: true
    }
  ],
  students: [
    {
      id: '3',
      name: "Coming Soon",
      description: "Interactive learning experiences designed to make education fun and engaging.",
      icon: "🎓",
      bg_color: "from-purple-50 to-pink-50",
      border_color: "border-purple-300",
      is_coming_soon: true
    }
  ]
};

const AIAppCorner = () => {
  const [apps, setApps] = useState({ kids: [], parents: [], students: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const response = await axios.get(`${API}/ai-apps`);
      if (response.data && response.data.length > 0) {
        // Group apps by category
        const grouped = {
          kids: response.data.filter(app => app.category === 'kids'),
          parents: response.data.filter(app => app.category === 'parents'),
          students: response.data.filter(app => app.category === 'students')
        };
        // Use defaults if a category is empty
        setApps({
          kids: grouped.kids.length > 0 ? grouped.kids : defaultApps.kids,
          parents: grouped.parents.length > 0 ? grouped.parents : defaultApps.parents,
          students: grouped.students.length > 0 ? grouped.students : defaultApps.students
        });
      } else {
        // Use default apps if database is empty
        setApps(defaultApps);
      }
    } catch (error) {
      console.error('Error fetching AI apps:', error);
      // Use default apps on error
      setApps(defaultApps);
    } finally {
      setLoading(false);
    }
  };

  const AppCard = ({ app, isKidApp = false }) => (
    <div
      className={`bg-gradient-to-br ${app.bg_color} p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 ${app.border_color} ${app.is_coming_soon ? 'opacity-75' : 'transform hover:-translate-y-1'}`}
      data-testid={`app-card-${app.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start gap-4">
        <div className="text-5xl animate-float">{app.icon}</div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-800 mb-2">{app.name}</h3>
          <p className="text-gray-600 mb-3 text-sm leading-relaxed">{app.description}</p>
          
          {isKidApp && !app.is_coming_soon && (
            <div className="flex flex-wrap gap-2 mb-4">
              {app.age_range && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {app.age_range}
                </span>
              )}
              {app.duration && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  ⏱️ {app.duration}
                </span>
              )}
            </div>
          )}
          
          {!app.is_coming_soon && app.url ? (
            <a
              href={app.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg"
              data-testid={`open-app-${app.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              Open App
              <ExternalLink className="ml-2" size={18} />
            </a>
          ) : (
            <span className="inline-flex items-center px-6 py-3 bg-gray-200 text-gray-500 font-semibold rounded-lg cursor-not-allowed">
              Coming Soon
              <Sparkles className="ml-2" size={18} />
            </span>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-white" data-testid="ai-app-corner-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-64 h-64 bg-yellow-300 rounded-full mix-blend-overlay filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-pink-300 rounded-full mix-blend-overlay filter blur-3xl animate-pulse delay-1000"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <Sparkles className="w-16 h-16 text-yellow-300 animate-pulse" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" data-testid="ai-corner-title">
              AI App Corner
            </h1>
            <p className="text-lg md:text-xl text-indigo-100 max-w-3xl mx-auto leading-relaxed" data-testid="ai-corner-intro">
              Our accounting & business advisory firm also builds AI-powered educational tools and stories for families, small business owners, and sparks creativity for kids through interactive stories and tools.
            </p>
          </div>
        </div>
      </section>

      {/* Kid-Safe Notice */}
      <section className="py-6 bg-green-50 border-y border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3">
            <Shield className="w-6 h-6 text-green-600" />
            <p className="text-green-800 text-sm md:text-base font-medium text-center" data-testid="kid-safe-notice">
              <span className="font-bold">Kid-Safe Zone:</span> These apps are for educational and entertainment purposes only and do not collect children's personal information.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Apps Section - Takes 2 columns */}
          <div className="lg:col-span-2 space-y-12">
            {/* For Kids Section */}
            <section data-testid="for-kids-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-red-100 rounded-xl">
                  <BookOpen className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-800">For Kids</h2>
                  <p className="text-gray-500 text-sm">Fun, safe, and educational apps</p>
                </div>
              </div>
              <div className="space-y-4">
                {apps.kids.map((app) => (
                  <AppCard key={app.id} app={app} isKidApp={true} />
                ))}
              </div>
            </section>

            {/* For Parents & Clients Section */}
            <section data-testid="for-parents-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-800">For Parents & Clients</h2>
                  <p className="text-gray-500 text-sm">Tools and resources for grown-ups</p>
                </div>
              </div>
              <div className="space-y-4">
                {apps.parents.map((app) => (
                  <AppCard key={app.id} app={app} />
                ))}
              </div>
            </section>

            {/* For Students/Learners Section */}
            <section data-testid="for-students-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <GraduationCap className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-800">For Students & Learners</h2>
                  <p className="text-gray-500 text-sm">Interactive learning experiences</p>
                </div>
              </div>
              <div className="space-y-4">
                {apps.students.map((app) => (
                  <AppCard key={app.id} app={app} />
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar - Work With Us Panel */}
          <div className="lg:col-span-1">
            <div className="sticky top-28">
              <div className="bg-gradient-to-br from-blue-800 to-indigo-900 rounded-2xl shadow-xl p-6 text-white" data-testid="work-with-us-panel">
                <h3 className="text-xl font-bold mb-4">Work With Us</h3>
                <p className="text-blue-100 mb-6 text-sm leading-relaxed">
                  Looking for professional services to help grow your business? We offer bookkeeping, accounting, payroll, and business automation services tailored to your needs.
                </p>
                <div className="space-y-3">
                  <Link
                    to="/services"
                    className="flex items-center justify-center w-full px-4 py-3 bg-white text-blue-800 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-200"
                    data-testid="view-services-btn"
                  >
                    View Our Services
                    <ArrowRight className="ml-2" size={18} />
                  </Link>
                  <Link
                    to="/contact"
                    className="flex items-center justify-center w-full px-4 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-800 transition-all duration-200"
                    data-testid="contact-us-btn"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>

              {/* Additional Info Card */}
              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-xl p-5" data-testid="ps-tip">
                <p className="text-yellow-800 text-sm">
                  <span className="font-bold">P.S.</span> If you have kids, they might enjoy our free AI-powered story apps! Share them with friends and family.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAppCorner;
